import { motion } from "framer-motion";
import { useEffect, useContext, useState } from "react";
import { LanguageContext } from "../../../context/LanguageContext";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.6 },
  }),
};

export default function Pricing() {
  const { t, isRTL } = useContext(LanguageContext);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const plans = t("pricing.plans", { returnObjects: true }) || [];

  const [selectedAddons, setSelectedAddons] = useState({});
  const [userQuantities, setUserQuantities] = useState({});

  const calculateTotal = (plan, index) => {
    let total = plan.price;
    const addons = selectedAddons[index] || [];

    addons.forEach((addon) => {
      if (addon.type === "perUser") {
        const qty = userQuantities[index] || 1;
        total += addon.price * qty;
      } else {
        total += addon.price;
      }
    });

    return total;
  };

  return (
    <section dir={isRTL ? "rtl" : "ltr"} className="bg-white py-24">
      <div className="mx-auto max-w-7xl px-6">

        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mb-20 text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
            {t("pricing.title")}
          </h1>
          <p className="mt-6 max-w-3xl mx-auto text-lg text-gray-600">
            {t("pricing.subtitle")}{" "}
            <strong>{t("pricing.discounts")}</strong>
          </p>
        </motion.div>

        {/* Plans */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className={`rounded-3xl border p-8 flex flex-col ${
                plan.highlighted
                  ? "border-rose-600 bg-rose-50"
                  : "border-gray-200 bg-white"
              }`}
            >
              <h3 className="text-xl font-semibold mb-2">
                {plan.name}
              </h3>

              {/* Dynamic Price */}
              <div className="mt-6 flex items-end gap-1">
                <span className="text-4xl font-extrabold text-black">
                  {plan.price === 0
                    ? "Gratuit"
                    : `${calculateTotal(plan, i).toFixed(2)}€`}
                </span>
                <span className="text-sm text-gray-500">
                  {plan.period || "/mois"}
                </span>
              </div>

              <ul className="space-y-3 text-sm flex-1">
                {plan.features.map((f, idx) => (
                  <li key={idx}>• {f}</li>
                ))}
              </ul>

              {/* Addons */}
              {plan.addons && plan.addons.length > 0 && (
                <div className="mt-6 space-y-4 border-t pt-4">
                  <p className="text-sm font-semibold text-gray-800">
                    Optional Add-ons
                  </p>

                  {plan.addons.map((addon, idx) => {
                    const isChecked =
                      selectedAddons[i]?.some(
                        (a) => a.label === addon.label
                      ) || false;

                    return (
                      <div key={idx} className="space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              setSelectedAddons((prev) => {
                                const current = prev[i] || [];

                                if (e.target.checked) {
                                  return {
                                    ...prev,
                                    [i]: [...current, addon],
                                  };
                                } else {
                                  return {
                                    ...prev,
                                    [i]: current.filter(
                                      (a) => a.label !== addon.label
                                    ),
                                  };
                                }
                              });
                            }}
                          />
                          {addon.label} (+{addon.price}€
                          {addon.type === "perUser" ? "/user" : ""}/mois)
                        </label>

                        {addon.type === "perUser" && isChecked && (
                          <div className="ml-6 flex items-center gap-3">
                            <span className="text-sm text-gray-600">
                              Number of users:
                            </span>

                            <input
                              type="number"
                              min="1"
                              value={userQuantities[i] || 1}
                              onChange={(e) =>
                                setUserQuantities({
                                  ...userQuantities,
                                  [i]: parseInt(e.target.value) || 1,
                                })
                              }
                              className="w-20 rounded-md border px-2 py-1 text-sm"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <a
                href={plan.name === "Enterprise" ? "/contact" : "/signup"}
                className={`mt-6 rounded-xl px-4 py-3 text-center font-medium ${
                  plan.highlighted
                    ? "bg-rose-600 text-white"
                    : "bg-gray-900 text-white"
                }`}
              >
                {plan.cta}
              </a>
            </motion.div>
          ))}
        </div>
<motion.div variants={fadeUp} className="mt-24 max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold mb-4">
              {t("pricing.billing.title")}
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              {t("pricing.billing.items", { returnObjects: true }).map(
                (item, i) => (
                  <li key={i}>{item}</li>
                )
              )}
            </ul>
          </motion.div>

          {/* Add-ons */}
          <motion.div variants={fadeUp} className="mt-16 max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold mb-4">
              {t("pricing.addons.title")}
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              {t("pricing.addons.items", { returnObjects: true }).map(
                (item, i) => (
                  <li key={i}>{item}</li>
                )
              )}
            </ul>
          </motion.div>

      </div>
    </section>
  );
}
